import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const sectorColors = {
  retail: { color: "var(--color-primary)", bg: "rgba(0,217,255,0.08)", border: "rgba(0,217,255,0.2)" },
  tech: { color: "#A78BFA", bg: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.2)" },
  manufacturing: { color: "var(--color-accent)", bg: "rgba(255,184,0,0.08)", border: "rgba(255,184,0,0.2)" },
  financial: { color: "var(--color-success)", bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.2)" },
  black_market: { color: "var(--color-error)", bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.2)" },
};

const sectorIcons = {
  retail: "ShoppingCart",
  tech: "Cpu",
  manufacturing: "Factory",
  financial: "Landmark",
  black_market: "Skull",
};

const statusConfig = {
  active: { label: "Active", color: "var(--color-success)", icon: "CheckCircle" },
  upgrading: { label: "Upgrading", color: "var(--color-accent)", icon: "ArrowUpCircle" },
  at_risk: { label: "At Risk", color: "var(--color-error)", icon: "AlertTriangle" },
  idle: { label: "Idle", color: "var(--color-muted-foreground)", icon: "PauseCircle" },
};

const BusinessCard = ({ business, onManage, onUpgrade, onSell }) => {
  const [expanded, setExpanded] = useState(false);
  const sector = sectorColors?.[business?.sector] || sectorColors?.retail;
  const sectorIcon = sectorIcons?.[business?.sector] || "Building2";
  const status = statusConfig?.[business?.status] || statusConfig?.active;
  const profitMargin = ((business?.revenue - business?.operatingCost) / business?.revenue * 100)?.toFixed(1);
  const netIncome = business?.revenue - business?.operatingCost;

  return (
    <div
      className="rounded-xl border transition-all duration-250 overflow-hidden"
      style={{ background: "var(--color-card)", borderColor: sector?.border }}
    >
      {/* Header */}
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: sector?.bg, border: `1px solid ${sector?.border}` }}>
              <Icon name={sectorIcon} size={18} color={sector?.color} />
            </div>
            <div className="min-w-0">
              <h3 className="font-heading text-sm font-semibold text-foreground truncate">{business?.name}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="font-caption text-xs capitalize" style={{ color: sector?.color }}>
                  {business?.sector?.replace("_", " ")}
                </span>
                <span className="text-muted-foreground text-xs">•</span>
                <span className="font-caption text-xs text-muted-foreground">Lvl {business?.level}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: `${status?.color}15` }}>
              <Icon name={status?.icon} size={11} color={status?.color} />
              <span className="font-caption text-xs" style={{ color: status?.color }}>{status?.label}</span>
            </div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-7 h-7 rounded flex items-center justify-center hover:bg-white/5 transition-colors"
            >
              <Icon name={expanded ? "ChevronUp" : "ChevronDown"} size={14} color="var(--color-muted-foreground)" />
            </button>
          </div>
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="rounded-lg p-2.5 text-center" style={{ background: "rgba(16,185,129,0.08)" }}>
            <div className="font-caption text-xs text-muted-foreground mb-0.5">Revenue</div>
            <div className="font-heading text-sm font-semibold" style={{ color: "var(--color-success)" }}>
              ${business?.revenue?.toLocaleString()}
            </div>
          </div>
          <div className="rounded-lg p-2.5 text-center" style={{ background: "rgba(239,68,68,0.08)" }}>
            <div className="font-caption text-xs text-muted-foreground mb-0.5">Op. Cost</div>
            <div className="font-heading text-sm font-semibold" style={{ color: "var(--color-error)" }}>
              ${business?.operatingCost?.toLocaleString()}
            </div>
          </div>
          <div className="rounded-lg p-2.5 text-center" style={{ background: netIncome >= 0 ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)" }}>
            <div className="font-caption text-xs text-muted-foreground mb-0.5">Net</div>
            <div className="font-heading text-sm font-semibold" style={{ color: netIncome >= 0 ? "var(--color-success)" : "var(--color-error)" }}>
              ${netIncome?.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Territory Bonus */}
        {business?.territoryBonus > 0 && (
          <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg" style={{ background: "rgba(255,184,0,0.08)", border: "1px solid rgba(255,184,0,0.15)" }}>
            <Icon name="MapPin" size={13} color="var(--color-accent)" />
            <span className="font-caption text-xs text-muted-foreground">Territory Bonus:</span>
            <span className="font-caption text-xs font-semibold" style={{ color: "var(--color-accent)" }}>+{business?.territoryBonus}% Revenue</span>
            <span className="font-caption text-xs text-muted-foreground ml-auto">{business?.territory}</span>
          </div>
        )}
      </div>
      {/* Expanded Details */}
      {expanded && (
        <div className="border-t px-4 md:px-5 py-4 space-y-4" style={{ borderColor: "var(--color-border)", background: "rgba(0,0,0,0.2)" }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="font-caption text-xs text-muted-foreground mb-1">Profit Margin</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, parseFloat(profitMargin)))}%`, background: parseFloat(profitMargin) > 30 ? "var(--color-success)" : parseFloat(profitMargin) > 10 ? "var(--color-accent)" : "var(--color-error)" }} />
                </div>
                <span className="font-caption text-xs text-foreground whitespace-nowrap">{profitMargin}%</span>
              </div>
            </div>
            <div>
              <div className="font-caption text-xs text-muted-foreground mb-1">Employees</div>
              <div className="flex items-center gap-1.5">
                <Icon name="Users" size={12} color="var(--color-primary)" />
                <span className="font-caption text-xs text-foreground">{business?.employees}</span>
              </div>
            </div>
            <div>
              <div className="font-caption text-xs text-muted-foreground mb-1">Upgrade Cost</div>
              <div className="font-caption text-xs text-foreground">${business?.upgradeCost?.toLocaleString()}</div>
            </div>
            <div>
              <div className="font-caption text-xs text-muted-foreground mb-1">Est. Value</div>
              <div className="font-caption text-xs text-foreground">${business?.estimatedValue?.toLocaleString()}</div>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button variant="default" size="sm" iconName="Settings" iconPosition="left" onClick={() => onManage(business)}>
              Manage
            </Button>
            <Button variant="outline" size="sm" iconName="ArrowUpCircle" iconPosition="left" onClick={() => onUpgrade(business)} disabled={business?.status === "upgrading"}>
              Upgrade
            </Button>
            <Button variant="destructive" size="sm" iconName="DollarSign" iconPosition="left" onClick={() => onSell(business)}>
              Sell
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCard;